/**
 * S2S postback tracking — operator page (feature #159).
 *
 * Surface for `/api/v1/s2s-postback`:
 *
 *   1. Fire URL helper — renders the tenant's inbound postback URL so the
 *      operator can copy it into an advertiser's platform.
 *
 *   2. Add-template form — scope to an advertiser, label, URL template,
 *      method, events allowlist. Client-side validation mirrors the server.
 *
 *   3. Templates table — list of registered outbound postback URL templates,
 *      filtered optionally by advertiser.
 *
 *   4. Simulate panel — dry-run macro substitution for a click_id so the
 *      operator can verify the rendered URL before going live.
 *
 *   5. Events log — the last N inbound postbacks with status + rendered URLs.
 *
 * Brand: emerald → sky gradient headline, Inter, zinc borders, 6-8px rounded,
 * light theme only. No emojis, no competitor names.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createUrl,
	deleteUrl,
	EMPTY_TEMPLATE_DRAFT,
	extractMacros,
	formatTimestamp,
	listEvents,
	listUrls,
	MACRO_DESCRIPTIONS,
	renderPreview,
	s2sPostbackQueryKeys,
	simulate,
	SUPPORTED_MACROS,
	STATUS_LABELS,
	statusBadgeClass,
	templateDraftSchema,
	type ClientMacroCtx,
	type PostbackEventView,
	type PostbackUrlView,
	type SimulateResponse,
	type TemplateDraft,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens — brand rules: emerald → sky, zinc borders, 6-8px, light only.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT = INPUT;
const HINT = "text-xs text-zinc-500";
const ERROR_TEXT = "text-xs text-red-600";
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const CODE =
	"rounded-[6px] border border-zinc-200 bg-zinc-50 px-2 py-1 font-mono text-xs text-zinc-800";

// ---------------------------------------------------------------------------
// Props — deep-link advertiser scope.
// ---------------------------------------------------------------------------

export interface S2sPostbackPageProps {
	initialAdvertiserId?: string;
	/** Base URL advertisers should ping (for the "Your postback URL" card). */
	inboundBaseUrl?: string;
}

export function S2sPostbackPage({
	initialAdvertiserId,
	inboundBaseUrl,
}: S2sPostbackPageProps) {
	const queryClient = useQueryClient();

	const [scope, setScope] = useState<string>(initialAdvertiserId ?? "");
	const [draft, setDraft] = useState<TemplateDraft>({
		...EMPTY_TEMPLATE_DRAFT,
		advertiser_id: initialAdvertiserId ?? "",
	});
	const [draftErrors, setDraftErrors] = useState<Record<string, string>>({});
	const [simulateClickId, setSimulateClickId] = useState<string>("");
	const [simulateEvent, setSimulateEvent] = useState<string>("conversion");
	const [simulatePayout, setSimulatePayout] = useState<string>("0");
	const [simulateTxn, setSimulateTxn] = useState<string>("");
	const [simulateResult, setSimulateResult] = useState<SimulateResponse | null>(null);

	const trimmedScope = scope.trim();

	const urlsQuery = useQuery({
		queryKey: s2sPostbackQueryKeys.urls(trimmedScope || undefined),
		queryFn: () => listUrls(trimmedScope || undefined),
	});

	const eventsQuery = useQuery({
		queryKey: s2sPostbackQueryKeys.events(),
		queryFn: () => listEvents(100),
	});

	const createMutation = useMutation({
		mutationFn: createUrl,
		onSuccess: (row: PostbackUrlView) => {
			toast.success(`Template "${row.label}" registered.`);
			setDraft({
				...EMPTY_TEMPLATE_DRAFT,
				advertiser_id: row.advertiser_id,
			});
			setDraftErrors({});
			queryClient.invalidateQueries({
				queryKey: s2sPostbackQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to register template.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (id: string) => deleteUrl(id),
		onSuccess: () => {
			toast.success("Template removed.");
			queryClient.invalidateQueries({ queryKey: s2sPostbackQueryKeys.all });
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to remove template.");
		},
	});

	const simulateMutation = useMutation({
		mutationFn: simulate,
		onSuccess: (res) => {
			setSimulateResult(res);
			if (res.rendered.length === 0) {
				toast("No templates matched.", { description: "Check event allowlist." });
			} else {
				toast.success(`Rendered ${res.rendered.length} URL(s).`);
			}
		},
		onError: (err: Error) => {
			setSimulateResult(null);
			toast.error(err.message || "Simulate failed.");
		},
	});

	function submitDraft() {
		const parsed = templateDraftSchema.safeParse(draft);
		if (!parsed.success) {
			setDraftErrors(parsed.errors);
			return;
		}
		setDraftErrors({});
		createMutation.mutate(parsed.data);
	}

	function submitSimulate() {
		const clickId = simulateClickId.trim();
		if (!clickId) {
			toast.error("Click ID is required for simulate.");
			return;
		}
		simulateMutation.mutate({
			click_id: clickId,
			event: simulateEvent || "conversion",
			payout: simulatePayout || 0,
			transaction_id: simulateTxn.trim() || null,
		});
	}

	const previewCtx: ClientMacroCtx = {
		click_id: simulateClickId.trim() || "<click_id>",
		event: simulateEvent || "conversion",
		payout: simulatePayout || 0,
		currency: "USD",
		transaction_id: simulateTxn.trim() || null,
		advertiser_id: draft.advertiser_id || "<advertiser_id>",
		offer_id: "<offer_id>",
		affiliate_id: "<affiliate_id>",
		sub1: null,
		sub2: null,
		sub3: null,
		sub4: null,
		sub5: null,
		timestamp: Math.floor(Date.now() / 1000),
	};

	const localPreview = useMemo(() => {
		if (!draft.url_template.trim()) return "";
		return renderPreview(draft.url_template, previewCtx);
	}, [draft.url_template, previewCtx]);

	const tokens = useMemo(() => extractMacros(draft.url_template), [draft.url_template]);

	const inboundExample = useMemo(() => {
		const base = (inboundBaseUrl ?? "https://api.example.com").replace(/\/+$/, "");
		return `${base}/api/v1/s2s-postback/fire?click_id={click_id}&event=conversion&payout={payout}&transaction_id={txn}`;
	}, [inboundBaseUrl]);

	return (
		<main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 pb-24 pt-8">
			<header className="flex flex-col gap-2">
				<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
					S2S postback tracking
				</h1>
				<p className="text-sm text-zinc-600">
					Accept inbound conversions by click_id, substitute macros, dedup
					replays, and fan-out to partner postback URLs. Supports GET and POST.
				</p>
			</header>

			{/* Inbound URL helper */}
			<section className={`${CARD} p-5`} aria-labelledby="inbound-hdr">
				<h2 id="inbound-hdr" className="text-base font-semibold text-zinc-900">
					Your inbound postback URL
				</h2>
				<p className={`${HINT} mt-1`}>
					Give this URL to the advertiser. They replace {"{click_id}"} with the
					id they were handed on click. Events dedup on
					(click_id, event, transaction_id) within 24h.
				</p>
				<div className="mt-3 flex flex-wrap items-center gap-2">
					<code className={`${CODE} break-all`}>{inboundExample}</code>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => {
							navigator.clipboard
								?.writeText(inboundExample)
								.then(() => toast.success("Copied."))
								.catch(() => toast.error("Copy failed."));
						}}
					>
						Copy
					</button>
				</div>
			</section>

			{/* Scope picker */}
			<section className={`${CARD} p-5`} aria-labelledby="scope-hdr">
				<h2 id="scope-hdr" className="text-base font-semibold text-zinc-900">
					Scope
				</h2>
				<div className="mt-3 flex flex-wrap items-end gap-3">
					<div className="flex flex-col gap-1">
						<label htmlFor="scope-adv" className={LABEL}>
							Advertiser ID (filter)
						</label>
						<input
							id="scope-adv"
							className={INPUT}
							value={scope}
							onChange={(e) => setScope(e.target.value)}
							placeholder="adv_1"
						/>
					</div>
					<p className={HINT}>Leave blank to show all advertisers.</p>
				</div>
			</section>

			{/* Add-template */}
			<section className={`${CARD} p-5`} aria-labelledby="add-hdr">
				<h2 id="add-hdr" className="text-base font-semibold text-zinc-900">
					Add postback URL template
				</h2>
				<div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
					<div className="flex flex-col gap-1">
						<label htmlFor="adv" className={LABEL}>
							Advertiser ID
						</label>
						<input
							id="adv"
							className={INPUT}
							value={draft.advertiser_id}
							onChange={(e) =>
								setDraft((p) => ({ ...p, advertiser_id: e.target.value }))
							}
							placeholder="adv_1"
						/>
						{draftErrors.advertiser_id && (
							<p className={ERROR_TEXT}>{draftErrors.advertiser_id}</p>
						)}
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="lbl" className={LABEL}>
							Label
						</label>
						<input
							id="lbl"
							className={INPUT}
							value={draft.label}
							onChange={(e) =>
								setDraft((p) => ({ ...p, label: e.target.value }))
							}
							placeholder="Partner A"
						/>
						{draftErrors.label && <p className={ERROR_TEXT}>{draftErrors.label}</p>}
					</div>
					<div className="flex flex-col gap-1 md:col-span-2">
						<label htmlFor="url" className={LABEL}>
							URL template
						</label>
						<input
							id="url"
							className={INPUT}
							value={draft.url_template}
							onChange={(e) =>
								setDraft((p) => ({ ...p, url_template: e.target.value }))
							}
							placeholder="https://partner.example/pb?click={click_id}&payout={payout}"
						/>
						{draftErrors.url_template && (
							<p className={ERROR_TEXT}>{draftErrors.url_template}</p>
						)}
						{tokens.length > 0 && (
							<p className={HINT}>
								Detected macros:{" "}
								{tokens.map((t, i) => (
									<span key={t}>
										<code className={CODE}>{`{${t}}`}</code>
										{i < tokens.length - 1 ? " " : ""}
									</span>
								))}
							</p>
						)}
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="method" className={LABEL}>
							Method
						</label>
						<select
							id="method"
							className={SELECT}
							value={draft.method}
							onChange={(e) =>
								setDraft((p) => ({
									...p,
									method: e.target.value === "POST" ? "POST" : "GET",
								}))
							}
						>
							<option value="GET">GET</option>
							<option value="POST">POST</option>
						</select>
					</div>
					<div className="flex flex-col gap-1">
						<label htmlFor="events" className={LABEL}>
							Events allowlist (CSV or *)
						</label>
						<input
							id="events"
							className={INPUT}
							value={draft.events_csv}
							onChange={(e) =>
								setDraft((p) => ({ ...p, events_csv: e.target.value }))
							}
							placeholder="conversion,install"
						/>
						{draftErrors.events_csv && (
							<p className={ERROR_TEXT}>{draftErrors.events_csv}</p>
						)}
					</div>
					<label className="col-span-1 mt-1 flex items-center gap-2 text-sm text-zinc-700 md:col-span-2">
						<input
							type="checkbox"
							checked={draft.enabled}
							onChange={(e) =>
								setDraft((p) => ({ ...p, enabled: e.target.checked }))
							}
							className="h-4 w-4 rounded border-zinc-300 text-emerald-600 focus:ring-emerald-500"
						/>
						Enabled (fire on matching events)
					</label>
					{localPreview && (
						<div className="col-span-1 flex flex-col gap-1 md:col-span-2">
							<span className={LABEL}>Local preview</span>
							<code className={`${CODE} break-all`}>{localPreview}</code>
							<p className={HINT}>
								Server-side render is authoritative; this is a client preview
								using the current click_id + event fields.
							</p>
						</div>
					)}
				</div>
				<div className="mt-4 flex items-center gap-2">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={submitDraft}
						disabled={createMutation.isPending}
					>
						{createMutation.isPending ? "Registering…" : "Register template"}
					</button>
					<button
						type="button"
						className={BTN_SECONDARY}
						onClick={() => {
							setDraft({
								...EMPTY_TEMPLATE_DRAFT,
								advertiser_id: scope,
							});
							setDraftErrors({});
						}}
					>
						Reset
					</button>
				</div>
			</section>

			{/* Templates table */}
			<section className={`${CARD} p-5`} aria-labelledby="tpl-hdr">
				<div className="flex items-center justify-between">
					<h2 id="tpl-hdr" className="text-base font-semibold text-zinc-900">
						Registered templates
					</h2>
					<span className={HINT}>
						{urlsQuery.data?.length ?? 0} total
					</span>
				</div>
				<div className="mt-3 overflow-x-auto">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="px-3 py-2">Label</th>
								<th className="px-3 py-2">Advertiser</th>
								<th className="px-3 py-2">Method</th>
								<th className="px-3 py-2">Events</th>
								<th className="px-3 py-2">Enabled</th>
								<th className="px-3 py-2">URL</th>
								<th className="px-3 py-2" />
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{(urlsQuery.data ?? []).map((row) => (
								<PostbackUrlRow
									key={row.id}
									row={row}
									onDelete={() => deleteMutation.mutate(row.id)}
									deleting={deleteMutation.isPending}
								/>
							))}
							{!urlsQuery.isLoading && (urlsQuery.data ?? []).length === 0 && (
								<tr>
									<td colSpan={7} className="px-3 py-6 text-center text-zinc-500">
										No templates registered yet.
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</section>

			{/* Simulate */}
			<section className={`${CARD} p-5`} aria-labelledby="sim-hdr">
				<h2 id="sim-hdr" className="text-base font-semibold text-zinc-900">
					Simulate fire
				</h2>
				<p className={`${HINT} mt-1`}>
					Dry-run render against the stored click + templates. Does not log an
					event.
				</p>
				<div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
					<input
						className={INPUT}
						placeholder="click_id"
						value={simulateClickId}
						onChange={(e) => setSimulateClickId(e.target.value)}
					/>
					<input
						className={INPUT}
						placeholder="event"
						value={simulateEvent}
						onChange={(e) => setSimulateEvent(e.target.value)}
					/>
					<input
						className={INPUT}
						placeholder="payout"
						value={simulatePayout}
						onChange={(e) => setSimulatePayout(e.target.value)}
					/>
					<input
						className={INPUT}
						placeholder="transaction_id (optional)"
						value={simulateTxn}
						onChange={(e) => setSimulateTxn(e.target.value)}
					/>
				</div>
				<div className="mt-3">
					<button
						type="button"
						className={BTN_PRIMARY}
						onClick={submitSimulate}
						disabled={simulateMutation.isPending}
					>
						{simulateMutation.isPending ? "Running…" : "Run simulate"}
					</button>
				</div>
				{simulateResult && (
					<div className="mt-4 flex flex-col gap-2">
						<h3 className="text-sm font-medium text-zinc-800">
							Rendered URLs ({simulateResult.rendered.length})
						</h3>
						{simulateResult.rendered.length === 0 ? (
							<p className={HINT}>No templates matched.</p>
						) : (
							<ul className="flex flex-col gap-2">
								{simulateResult.rendered.map((r) => (
									<li key={r.template_id} className="flex flex-col gap-1">
										<span className="text-xs font-medium text-zinc-700">
											{r.label}{" "}
											<span className="text-zinc-400">· {r.method}</span>
										</span>
										<code className={`${CODE} break-all`}>{r.rendered_url}</code>
									</li>
								))}
							</ul>
						)}
					</div>
				)}
			</section>

			{/* Events log */}
			<section className={`${CARD} p-5`} aria-labelledby="evt-hdr">
				<div className="flex items-center justify-between">
					<h2 id="evt-hdr" className="text-base font-semibold text-zinc-900">
						Recent postback events
					</h2>
					<span className={HINT}>{eventsQuery.data?.length ?? 0} rows</span>
				</div>
				<div className="mt-3 overflow-x-auto">
					<table className="min-w-full divide-y divide-zinc-200 text-sm">
						<thead className="bg-zinc-50 text-left text-xs uppercase tracking-wide text-zinc-500">
							<tr>
								<th className="px-3 py-2">Status</th>
								<th className="px-3 py-2">Click</th>
								<th className="px-3 py-2">Event</th>
								<th className="px-3 py-2">Payout</th>
								<th className="px-3 py-2">Txn</th>
								<th className="px-3 py-2">Rendered</th>
								<th className="px-3 py-2">At</th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-100">
							{(eventsQuery.data ?? []).map((row) => (
								<EventRow key={row.id} row={row} />
							))}
							{!eventsQuery.isLoading &&
								(eventsQuery.data ?? []).length === 0 && (
									<tr>
										<td colSpan={7} className="px-3 py-6 text-center text-zinc-500">
											No events yet.
										</td>
									</tr>
								)}
						</tbody>
					</table>
				</div>
			</section>

			{/* Macro reference */}
			<section className={`${CARD} p-5`} aria-labelledby="ref-hdr">
				<h2 id="ref-hdr" className="text-base font-semibold text-zinc-900">
					Supported macros
				</h2>
				<div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
					{SUPPORTED_MACROS.map((k) => (
						<div
							key={k}
							className="flex items-center justify-between gap-3 rounded-[6px] border border-zinc-100 bg-zinc-50 px-3 py-2"
						>
							<code className="font-mono text-xs text-zinc-800">{`{${k}}`}</code>
							<span className="text-xs text-zinc-600">
								{MACRO_DESCRIPTIONS[k]}
							</span>
						</div>
					))}
				</div>
			</section>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PostbackUrlRow({
	row,
	onDelete,
	deleting,
}: {
	row: PostbackUrlView;
	onDelete: () => void;
	deleting: boolean;
}) {
	return (
		<tr className="align-top">
			<td className="px-3 py-2 text-zinc-900">{row.label}</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-xs">{row.advertiser_id}</code>
			</td>
			<td className="px-3 py-2 text-zinc-700">{row.method}</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-xs">{row.events_csv}</code>
			</td>
			<td className="px-3 py-2">
				<span
					className={`inline-flex rounded-[6px] border px-2 py-0.5 text-xs ${
						row.enabled
							? "bg-emerald-50 text-emerald-700 border-emerald-200"
							: "bg-zinc-50 text-zinc-600 border-zinc-200"
					}`}
				>
					{row.enabled ? "Enabled" : "Disabled"}
				</span>
			</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-[11px] break-all">{row.url_template}</code>
			</td>
			<td className="px-3 py-2 text-right">
				<button
					type="button"
					className="rounded-[6px] border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
					onClick={onDelete}
					disabled={deleting}
				>
					Remove
				</button>
			</td>
		</tr>
	);
}

function EventRow({ row }: { row: PostbackEventView }) {
	return (
		<tr className="align-top">
			<td className="px-3 py-2">
				<span
					className={`inline-flex rounded-[6px] border px-2 py-0.5 text-xs ${statusBadgeClass(
						row.status,
					)}`}
				>
					{STATUS_LABELS[row.status]}
				</span>
			</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-xs">{row.click_id}</code>
			</td>
			<td className="px-3 py-2 text-zinc-700">{row.event}</td>
			<td className="px-3 py-2 text-zinc-700">
				{row.payout.toFixed(2)} {row.currency}
			</td>
			<td className="px-3 py-2 text-zinc-700">
				<code className="font-mono text-xs">{row.transaction_id ?? "—"}</code>
			</td>
			<td className="px-3 py-2 text-zinc-700">
				{row.rendered_urls.length === 0 ? (
					<span className="text-zinc-400">—</span>
				) : (
					<span>{row.rendered_urls.length} URL(s)</span>
				)}
			</td>
			<td className="px-3 py-2 text-zinc-500 text-xs">
				{formatTimestamp(row.created_at)}
			</td>
		</tr>
	);
}

export default S2sPostbackPage;
